"use client";

import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";

type Props = {
    ariaLabel: string;
    instructions: string;
    children: ReactNode;
};

export default function BlogTableScrollRegion({ ariaLabel, instructions, children }: Props) {
    const regionRef = useRef<HTMLDivElement>(null);
    const instructionsId = useId();

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const region = regionRef.current;
        if (!region) return;

        const maxScrollLeft = Math.max(0, region.scrollWidth - region.clientWidth);
        const step = Math.max(96, Math.round(region.clientWidth * 0.7));
        let nextScrollLeft: number | null = null;

        if (event.key === "ArrowRight") nextScrollLeft = region.scrollLeft + step;
        if (event.key === "ArrowLeft") nextScrollLeft = region.scrollLeft - step;
        if (event.key === "Home") nextScrollLeft = 0;
        if (event.key === "End") nextScrollLeft = maxScrollLeft;
        if (nextScrollLeft === null) return;

        event.preventDefault();
        region.scrollTo({
            left: Math.min(maxScrollLeft, Math.max(0, nextScrollLeft)),
            behavior: "auto",
        });
    };

    return (
        <div
            ref={regionRef}
            className="overflow-x-auto overscroll-x-contain focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-600"
            role="region"
            aria-label={ariaLabel}
            aria-describedby={instructionsId}
            aria-keyshortcuts="ArrowLeft ArrowRight Home End"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            data-blog-table-scroll="true"
        >
            <span id={instructionsId} className="sr-only">{instructions}</span>
            {children}
        </div>
    );
}
